import { Component, ViewChild, Input, Output, EventEmitter } from '@angular/core';
import { DatePipe } from '@angular/common';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { BookingSummary, BookingSummaryResult, BookingCancellation, BookingStatus } from 'models';
import { BookingService, VerificationService } from 'services';
import * as clipboard from 'clipboard-polyfill';
import { ToastrService } from 'ngx-toastr';
import { MatMenuTrigger } from '@angular/material/menu';
import { CancelVerificationModalData } from 'models/cancel-verification-modal-data';
import { take } from 'rxjs/operators';

@Component({
	selector: 'app-booking-list',
	template: require('./booking-list.component.html'),
	styles: [require('./booking-list.component.scss')],
	providers: [DatePipe],
})
export class BookingListComponent {
	@Input() showVerificationLink: boolean;

	@Input() pageSize: number;

	@Input()
	get bookingSummary(): BookingSummary {
		return this.summary;
	}

	set bookingSummary(input: BookingSummary) {
		this.summary = input;
		this.init();
	}

	showCancelModal = false;
	modalData: CancelVerificationModalData;

	@Output() bookingCancelled: EventEmitter<BookingSummaryResult> = new EventEmitter();

	summary: BookingSummary;

	cancelledBookingId: number;

	// table source, sort & pagination
	@ViewChild(MatPaginator) paginator: MatPaginator;
	@ViewChild(MatSort) sort: MatSort;
	bookingSummaryResults: MatTableDataSource<BookingSummaryResult>;
	displayedColumns = ['guestName', 'addressLine1', 'checkIn', 'progress', 'guestBookingStatus', 'action'];

	length: number;
	pagedSortedFlattenedBookingData: BookingSummaryResult[] = [];
	sortedFlattenedBookingData: BookingSummaryResult[] = [];

	@ViewChild(MatMenuTrigger) trigger: MatMenuTrigger;

	constructor(private bookingService: BookingService, private verificationService: VerificationService, private toastrService: ToastrService, private pipe: DatePipe) {}

	openCancelModal(result: BookingSummaryResult): void {
		this.cancelledBookingId = result.bookingId;
		this.setCancelModalText(result);
		this.showCancelModal = true;
	}

	closeModal(): void {
		this.showCancelModal = false;
	}

	setCancelModalText(result: BookingSummaryResult): void {
		const checkIn = this.pipe.transform(result.checkIn, 'fullDate');

		this.modalData = {
			title: 'Cancel verification',
			cancellationHtml: `<p class="text-sm text-gray-500">Are you sure you want to cancel the booking verification for ${result.guestName} checking in at ${result.addressLine1} starting ${checkIn}?</p>`,
			cancelButton: 'Cancel verification',
			closeModal: 'Keep verification',
			verification: result,
		};
	}

	setModalText(result: BookingSummaryResult): void {
		const checkIn = this.pipe.transform(result.checkIn, 'fullDate');

		this.modalData = {
			title: 'Cancel verification',
			cancellationHtml: `<p class="text-sm text-gray-500">Are you sure you want to cancel the booking verification for ${result.guestName} checking in at ${result.addressLine1} starting ${checkIn}?</p>`,
			cancelButton: 'Cancel verification',
			closeModal: 'Keep verification',
			verification: result,
		};
	}

	init(): void {
		if (this.summary && this.summary.bookingSummaryResult) {
			this.summary.bookingSummaryResult.sort((a, b) => b.checkIn.getTime() - a.checkIn.getTime());
			this.sortedFlattenedBookingData = this.summary.bookingSummaryResult.slice();
			this.pagedSortedFlattenedBookingData = this.sortedFlattenedBookingData.slice(0, this.pageSize);
			this.length = this.summary.bookingSummaryResult.length;

			this.bookingSummaryResults = new MatTableDataSource<BookingSummaryResult>(this.sortedFlattenedBookingData);
			this.bookingSummaryResults.paginator = this.paginator;
			this.bookingSummaryResults.sort = this.sort;
		}
	}

	cancelBooking(): void {
		if (this.cancelledBookingId > 0) {
			const item = this.summary.bookingSummaryResult.find(x => x.bookingId === this.cancelledBookingId);
			const bookingCancellation = new BookingCancellation();
			bookingCancellation.id = item.bookingId;

			this.bookingService
				.cancellation(bookingCancellation)
				.pipe(take(1))
				.subscribe(() => {
					this.showCancelModal = false;
					item.guestBookingStatus = 'Cancelled';
				});
		}
		this.cancelledBookingId = 0;
	}

	isVerificationLinkActive(bookingSummaryResult: BookingSummaryResult) {
		return bookingSummaryResult.guestBookingStatus !== 'Rejected' && bookingSummaryResult.guestBookingStatus !== 'Cancelled' && bookingSummaryResult.verificationUrl !== null;
	}

	updateBookingStatus(item: BookingSummaryResult) {
		const index = this.summary.bookingSummaryResult.indexOf(item);
		this.summary.bookingSummaryResult[index].guestBookingStatus = BookingStatus.cancelled;
		this.summary.bookingSummaryResult[index].showCancellationButton = false;
		this.summary.bookingSummaryResult[index].showVerificationLink = false;
	}

	copyLink(verificationUrl: string) {
		clipboard
			.writeText(verificationUrl)
			.then(_ => {
				this.toastrService.success('Verification Link copied to clipboard');
			})
			.catch(err => {});
	}

	viewContract(contractVerificationId: number) {
		this.verificationService.viewContract(contractVerificationId.toString()).subscribe(
			blob => {
				const blobUrl = URL.createObjectURL(blob);
				window.open(blobUrl, '_blank');
			},
			() => {
				this.toastrService.error('Unable to download contract. It may not be ready to view so please try again shortly. If the problem persists then please contact support.');
			}
		);
	}
}
